import { useEffect, useState, useCallback, useMemo } from 'react';
import { useToast } from '../context/ToastContext';
import Loader from '../components/Loader';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchableSelect from '../components/SearchableSelect';
import AiAssistantPanel from '../components/AiAssistantPanel';
import api from '../utils/api';
import { todaySGT, formatDate } from '../utils/date';

const DAY_IDS = [
  { id: 0, label: 'MON' },
  { id: 1, label: 'TUE' },
  { id: 2, label: 'WED' },
  { id: 3, label: 'THU' },
  { id: 4, label: 'FRI' },
  { id: 5, label: 'SAT' },
  { id: 6, label: 'SUN' },
];
// Default hourly gridlines so Week View has something to click even before
// any session exists — any actual session time (however odd) is merged in
// alongside these, since start time itself is a free time picker, not
// limited to a fixed slot list.
const DEFAULT_TIME_MARKS = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// Flat weekly-hours cap applied to every trainer for the overload meter —
// there's no per-trainer max-hours column on `users` yet, so this is one
// shared default rather than a per-person figure.
const DEFAULT_MAX_HOURS = 20;
// How much gap between two sessions still counts as "no real break" for the
// back-to-back overload check.
const BACK_TO_BACK_GRACE_MINUTES = 15;

const EMPTY_MODAL = { mode: 'create', id: null, intake_code: '', module_code: '', trainer_id: '', session_date: '', start_time: '', end_time: '', room: '', repeat: 'none', repeatUntil: '', intakeModules: [], intakeModulesLoading: false, lock: null };

function normTime(t) { return t ? String(t).slice(0, 5) : t; }
function toMinutes(t) { if (!t) return NaN; const [h, m] = normTime(t).split(':').map(Number); return h * 60 + (m || 0); }
function minutesToTime(mins) { const wrapped = ((mins % 1440) + 1440) % 1440; return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`; }
function shortName(name) { return (name || '').replace(/^(Dr\.|Ms\.|Mr\.|Prof\.)\s/, ''); }
function initials(name = '') { return name.split(' ').slice(0, 2).map((w) => w[0] || '').join('').toUpperCase(); }
function mondayOnOrBefore(date) { const d = new Date(date); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); d.setHours(0, 0, 0, 0); return d; }
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function isoDate(date) { const d = new Date(date); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function parseIso(iso) { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); }
function formatDisplayDate(iso) {
  if (!iso) return '—';
  const dt = parseIso(iso);
  return `${WEEKDAY_NAMES[dt.getDay()].slice(0, 3)}, ${dt.getDate()} ${MONTH_SHORT[dt.getMonth()]} ${dt.getFullYear()}`;
}
function weekdayName(iso) { return iso ? WEEKDAY_NAMES[parseIso(iso).getDay()] : ''; }
function formatDuration(mins) {
  if (!Number.isFinite(mins) || mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}
// Builds the list of session_date strings a "repeat" schedule covers —
// daily skips weekends (this scheduler is Mon-Fri only), weekly lands on
// the same weekday as the start date every time.
function occurrenceDates(startIso, repeat, untilIso) {
  if (repeat === 'none' || !untilIso) return [startIso];
  const start = parseIso(startIso);
  const until = parseIso(untilIso);
  const stepDays = repeat === 'daily' ? 1 : 7;
  const dates = [];
  for (let cur = start; cur <= until; cur = addDays(cur, stepDays)) {
    if (repeat !== 'daily' || (cur.getDay() >= 1 && cur.getDay() <= 5)) dates.push(isoDate(cur));
  }
  return dates.length ? dates : [startIso];
}

export default function TimetablePage() {
  const { show } = useToast();
  const [sessions, setSessions] = useState([]);
  const [modules, setModules] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perms, setPerms] = useState(null);

  const [activeTab, setActiveTab] = useState('week');
  const [weekStart, setWeekStart] = useState(() => mondayOnOrBefore(new Date()));

  const [modal, setModal] = useState(null);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/records/timetable'),
      api.get('/modules'),
      api.get('/records/intakes'),
      api.get('/users/trainers'),
      api.get('/venues'),
      api.get('/users/me/permissions'),
    ])
      .then(([ttRes, modRes, intakeRes, trainerRes, venueRes, permsRes]) => {
        setSessions(ttRes.data.data);
        setModules(modRes.data.data);
        setIntakes(intakeRes.data.data);
        setTrainers(trainerRes.data.data);
        setVenues(venueRes.data.data);
        setPerms(permsRes.data.data.timetable_master || { view: false, create: false, update: false, delete: false });
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load timetable'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Only intakes currently running are schedulable — started (today >= SOC)
  // and not yet finished (today <= EOC), same rule the Course Catalogue uses
  // for "active intakes".
  const activeIntakes = useMemo(() => {
    const today = todaySGT();
    return intakes.filter((i) => {
      if (!i.is_active) return false;
      const soc = i.soc_date ? String(i.soc_date).slice(0, 10) : null;
      const eoc = i.eoc_date ? String(i.eoc_date).slice(0, 10) : null;
      if (!soc || !eoc) return false;
      return today >= soc && today <= eoc;
    });
  }, [intakes]);
  const moduleName = useCallback((code) => modules.find((m) => m.module_code === code)?.module_name || code, [modules]);
  const trainerName = useCallback((id) => trainers.find((t) => t.id === id)?.name || '—', [trainers]);
  const venueByName = useCallback((name) => venues.find((v) => v.name === name), [venues]);

  const weekEnd = addDays(weekStart, 6);
  const days = DAY_IDS.map((d) => {
    const dt = addDays(weekStart, d.id);
    return { ...d, date: `${dt.getDate()} ${MONTH_SHORT[dt.getMonth()]}`, fullDate: isoDate(dt) };
  });
  const weekStartIso = days[0].fullDate;
  const weekEndIso = days[days.length - 1].fullDate;

  // Trainer workload is scoped to the week currently on screen — a session
  // now sits on one real date, so "this week" means exactly the 7 dates the
  // grid shows, not every session ever scheduled.
  const trainerStats = useMemo(() => trainers.map((t) => {
    const mine = sessions.filter((s) => s.trainer_id === t.id && s.session_date >= weekStartIso && s.session_date <= weekEndIso);
    const hours = Math.round(mine.reduce((sum, s) => sum + (Number(s.duration_minutes) || 90) / 60, 0) * 10) / 10;
    let maxRun = 0;
    let maxRunDay = null;
    days.forEach((d) => {
      const daySessions = mine.filter((s) => s.session_date === d.fullDate).slice().sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time));
      let run = daySessions.length ? 1 : 0;
      let best = run;
      for (let i = 1; i < daySessions.length; i++) {
        const prevEnd = toMinutes(daySessions[i - 1].start_time) + (Number(daySessions[i - 1].duration_minutes) || 90);
        const curStart = toMinutes(daySessions[i].start_time);
        run = curStart <= prevEnd + BACK_TO_BACK_GRACE_MINUTES ? run + 1 : 1;
        if (run > best) best = run;
      }
      if (best > maxRun) { maxRun = best; maxRunDay = d; }
    });
    const pct = Math.min(100, Math.round((hours / DEFAULT_MAX_HOURS) * 100));
    const overloaded = maxRun >= 4 || hours >= DEFAULT_MAX_HOURS;
    return { ...t, hours, pct, maxRun, maxRunDay, overloaded };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [trainers, sessions, weekStartIso, weekEndIso]);
  const statById = useMemo(() => Object.fromEntries(trainerStats.map((t) => [t.id, t])), [trainerStats]);
  // Sidebar panels list every trainer/venue — sort busiest-first so a long
  // roster still surfaces who needs attention without scrolling.
  const sortedTrainerStats = useMemo(() => [...trainerStats].sort((a, b) => b.hours - a.hours), [trainerStats]);
  const sortedVenueStats = useMemo(
    () => venues.map((v) => ({ ...v, util: sessions.filter((s) => s.room === v.name).length })).sort((a, b) => b.util - a.util),
    [venues, sessions],
  );

  const timeSlots = useMemo(() => {
    const set = new Set([...DEFAULT_TIME_MARKS, ...sessions.map((s) => normTime(s.start_time))]);
    return Array.from(set).sort();
  }, [sessions]);

  const rangeLabel = weekStart.getMonth() === weekEnd.getMonth()
    ? `${weekStart.getDate()} – ${weekEnd.getDate()} ${MONTH_SHORT[weekStart.getMonth()]} ${weekStart.getFullYear()}`
    : `${weekStart.getDate()} ${MONTH_SHORT[weekStart.getMonth()]} – ${weekEnd.getDate()} ${MONTH_SHORT[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`;

  const overloadedTrainer = trainerStats.find((t) => t.overloaded && t.maxRun >= 4);

  // Module choices depend on which intake is selected (only modules actually
  // assigned to that intake), so picking Module only makes sense after
  // Intake — this fetches that intake's module list and drops it into
  // modal.intakeModules, guarded so a stale response can't clobber a
  // since-changed selection.
  const loadIntakeModules = async (intakeCode) => {
    const intake = intakes.find((i) => i.intake_code === intakeCode);
    if (!intake) { setModal((m) => (m ? { ...m, intakeModules: [], intakeModulesLoading: false } : m)); return; }
    setModal((m) => (m ? { ...m, intakeModulesLoading: true } : m));
    try {
      const res = await api.get(`/records/intakes/${intake.id}/modules`);
      setModal((m) => (m && m.intake_code === intakeCode ? { ...m, intakeModules: res.data.data, intakeModulesLoading: false } : m));
    } catch (err) {
      setModal((m) => (m && m.intake_code === intakeCode ? { ...m, intakeModules: [], intakeModulesLoading: false } : m));
    }
  };
  const onIntakeChange = (code) => {
    setModal((m) => ({ ...m, intake_code: code, module_code: '', intakeModules: [] }));
    if (code) loadIntakeModules(code);
  };

  const openAssign = (prefill, lock) => {
    if (!perms?.create) return;
    setModal({ ...EMPTY_MODAL, session_date: weekStartIso, ...prefill, lock: lock || null });
    setFormError(null);
    setAiSuggestion(null);
    setAiSuggestLoading(false);
    if (prefill?.intake_code) loadIntakeModules(prefill.intake_code);
  };
  const openEdit = (session, lock) => {
    if (!perms?.update) return;
    const startTime = normTime(session.start_time);
    const endTime = minutesToTime(toMinutes(startTime) + (Number(session.duration_minutes) || 90));
    setModal({ mode: 'edit', ...session, start_time: startTime, end_time: endTime, repeat: 'none', repeatUntil: '', intakeModules: [], intakeModulesLoading: false, lock: lock || null });
    setFormError(null);
    setAiSuggestion(null);
    setAiSuggestLoading(false);
    loadIntakeModules(session.intake_code);
  };
  const closeModal = () => { setModal(null); setFormError(null); setAiSuggestion(null); setAiSuggestLoading(false); };
  const patchModal = (patch) => setModal((m) => ({ ...m, ...patch }));
  // Auto-fills a sensible End Time the first time Start Time is set, so the
  // user isn't forced to type both — they can still adjust End Time after.
  const onStartTimeChange = (val) => {
    setModal((m) => {
      const next = { ...m, start_time: val };
      if (val && !m.end_time) next.end_time = minutesToTime(toMinutes(val) + 90);
      return next;
    });
  };

  const aiSuggest = async () => {
    if (!modal || !modal.start_time || !modal.end_time) { show('Pick a start and end time first', 'info'); return; }
    const modalDur = toMinutes(modal.end_time) - toMinutes(modal.start_time);
    if (modalDur <= 0) { show('End time must be after start time', 'info'); return; }
    setAiSuggestLoading(true);
    setAiSuggestion(null);
    try {
      const res = await api.post('/ai/scheduling-assistant/suggest-trainer', {
        sessionDate: modal.session_date,
        startTime: modal.start_time,
        endTime: modal.end_time,
        moduleCode: modal.module_code,
        intakeCode: modal.intake_code,
        excludeSessionId: modal.id,
        lockedRoom: modal.lock === 'room' ? modal.room : null,
        weekStartIso,
        weekEndIso,
      });
      const { trainerId, name, room, reason } = res.data.data;
      if (!trainerId || !room) { show(reason || 'No trainer/room is free at that date and time', 'info'); return; }
      setAiSuggestion({ trainerId, name: shortName(name), room, reason });
    } catch (err) {
      show(err.response?.data?.message || "Couldn't reach the AI assistant", 'error');
    } finally {
      setAiSuggestLoading(false);
    }
  };
  const useSuggestion = () => {
    if (!aiSuggestion) return;
    patchModal({ trainer_id: aiSuggestion.trainerId, room: aiSuggestion.room });
    setAiSuggestion(null);
  };

  const modalDurationMinutes = modal?.start_time && modal?.end_time ? toMinutes(modal.end_time) - toMinutes(modal.start_time) : NaN;
  const saveDisabled = !(
    modal?.module_code && modal?.trainer_id && modal?.intake_code && modal?.session_date
    && modal?.start_time && modal?.end_time && modal?.room && modalDurationMinutes > 0
    && (modal?.repeat === 'none' || modal?.repeatUntil)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saveDisabled) {
      setFormError(modal?.start_time && modal?.end_time && modalDurationMinutes <= 0
        ? 'End time must be after start time.'
        : 'Intake, Module, Trainer, Date, Start Time, End Time and Room are all required.');
      return;
    }
    setFormError(null);
    setSubmitting(true);
    const basePayload = {
      intake_code: modal.intake_code,
      module_code: modal.module_code,
      trainer_id: Number(modal.trainer_id),
      start_time: modal.start_time,
      duration_minutes: modalDurationMinutes,
      room: modal.room || null,
    };
    try {
      if (modal.mode === 'create') {
        const dates = modal.repeat !== 'none' && modal.repeatUntil
          ? occurrenceDates(modal.session_date, modal.repeat, modal.repeatUntil)
          : [modal.session_date];
        let created = 0;
        let failed = 0;
        for (const d of dates) {
          try {
            await api.post('/records/timetable', { ...basePayload, session_date: d });
            created++;
          } catch (err) {
            failed++;
          }
        }
        if (failed && !created) {
          setFormError('None of the sessions could be scheduled — every date conflicted with an existing booking.');
          return;
        }
        show(
          failed ? `${created} session(s) scheduled, ${failed} skipped due to conflicts` : (created > 1 ? `${created} sessions scheduled` : 'Session scheduled'),
          failed ? 'info' : 'success'
        );
      } else {
        await api.put(`/records/timetable/${modal.id}`, { ...basePayload, session_date: modal.session_date });
        let created = 0;
        let failed = 0;
        if (modal.repeat !== 'none' && modal.repeatUntil) {
          const extraDates = occurrenceDates(modal.session_date, modal.repeat, modal.repeatUntil).filter((d) => d !== modal.session_date);
          for (const d of extraDates) {
            try {
              await api.post('/records/timetable', { ...basePayload, session_date: d });
              created++;
            } catch (err) {
              failed++;
            }
          }
        }
        show(
          created || failed
            ? `Session updated${created ? `, ${created} additional session(s) scheduled` : ''}${failed ? `, ${failed} skipped due to conflicts` : ''}`
            : 'Session updated',
          failed ? 'info' : 'success'
        );
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || `Failed to ${modal.mode === 'create' ? 'schedule' : 'update'} session`);
    } finally {
      setSubmitting(false);
    }
  };

  const askRemove = () => {
    if (!modal?.id) return;
    setDeleteTarget({ id: modal.id, label: `${modal.module_code} on ${formatDisplayDate(modal.session_date)} ${modal.start_time}` });
    closeModal();
  };
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/records/timetable/${deleteTarget.id}`);
      show('Session removed', 'success');
      setDeleteTarget(null);
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to remove session', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const rows = timeSlots.map((time) => ({
    time,
    cells: days.map((d) => {
      const session = sessions.find((s) => s.session_date === d.fullDate && normTime(s.start_time) === time);
      if (!session) return { key: `${d.fullDate}-${time}`, empty: true, date: d.fullDate, time };
      const trStat = statById[session.trainer_id];
      const overloaded = !!(trStat?.overloaded && trStat.maxRunDay?.fullDate === session.session_date);
      return { key: `s-${session.id}`, empty: false, session, overloaded };
    }),
  }));

  const buildChip = (s) => {
    const trStat = statById[s.trainer_id];
    const overloaded = !!(trStat?.overloaded && trStat.maxRunDay?.fullDate === s.session_date);
    return { session: s, overloaded };
  };
  const lvRows = trainers.map((t) => ({
    trainer: t,
    cells: days.map((d) => ({
      date: d.fullDate,
      chips: sessions.filter((s) => s.trainer_id === t.id && s.session_date === d.fullDate).sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time)).map(buildChip),
    })),
  }));
  const rmRows = venues.map((v) => ({
    venue: v,
    cells: days.map((d) => ({
      date: d.fullDate,
      chips: sessions.filter((s) => s.room === v.name && s.session_date === d.fullDate).sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time)).map(buildChip),
    })),
  }));

  const intakeOptions = activeIntakes.map((i) => ({
    value: i.intake_code,
    label: i.intake_code,
    sublabel: [i.course_name, `${formatDate(i.soc_date)} → ${formatDate(i.eoc_date)}`].filter(Boolean).join(' · '),
  }));
  const moduleOptions = (modal?.intakeModules || []).filter((m) => m.is_active).map((m) => ({ value: m.module_code, label: `${m.module_code} — ${m.module_name}` }));
  const trainerOptions = trainers.map((t) => ({ value: t.id, label: t.name }));
  const roomOptions = venues.map((v) => ({ value: v.name, label: v.name }));

  if (!loading && !error && perms && !perms.view) {
    return (
      <div>
        <div className="sh-ey">COURSE DEPLOYMENT · TIMETABLE SCHEDULER</div>
        <div className="sh-ti">Weekly Scheduling</div>
        <div className="alert"><i className="fa-solid fa-lock" /><div className="alert-t">You do not have access to the Timetable Scheduler.</div></div>
      </div>
    );
  }

  return (
    <div>
      <div className="sh-ey">COURSE DEPLOYMENT · TIMETABLE SCHEDULER</div>
      <div className="sh-ti">Weekly Scheduling</div>
      <div className="sh-de">Assign trainers to modules and intakes. Trainer workload is checked in real time, with a suggestion for who's free to teach.</div>

      {loading && <Loader label="Loading timetable…" />}
      {error && !loading && <div className="alert"><i className="fa-solid fa-triangle-exclamation" /><div className="alert-t">{error}</div></div>}

      {!loading && !error && (
        <>
          <div className="tt-view-bar">
            <div className="tabs">
              {['week', 'lecturer', 'room'].map((v) => (
                <div key={v} className={`tab ${activeTab === v ? 'active' : ''}`} onClick={() => setActiveTab(v)}>
                  {v === 'week' ? 'Week View' : v === 'lecturer' ? 'Lecturer View' : 'Room View'}
                </div>
              ))}
            </div>
            {perms?.create && (
              <button type="button" className="btn btn-primary tt-schedule-btn" onClick={() => openAssign({})}>
                <i className="fa-solid fa-plus" /> Schedule Session
              </button>
            )}
          </div>

          {overloadedTrainer && (
            <div className="tt-alert">
              <i className="fa-solid fa-gauge-high" />
              <div>
                <div className="tt-alert-t">AI Workload Alert — {overloadedTrainer.name} is overloaded</div>
                <div className="tt-alert-d">Back-to-back {overloadedTrainer.maxRun} sessions on {overloadedTrainer.maxRunDay.label} ({overloadedTrainer.maxRunDay.date}) with no break between classes.</div>
              </div>
              <button type="button" className="btn btn-sm tt-alert-a" style={{ background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid var(--red)' }} onClick={() => setActiveTab('lecturer')}>Review Sessions</button>
            </div>
          )}

          <div className="tt-shell">
            <div className="tt-main">
              <div className="tt-tb">
                <div className="tt-tb-selects">
                  <input
                    type="date"
                    className="finput tt-tb-date"
                    value={isoDate(weekStart)}
                    onChange={(e) => { if (e.target.value) setWeekStart(mondayOnOrBefore(parseIso(e.target.value))); }}
                  />
                </div>
                <div className="tt-tb-nav">
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWeekStart(addDays(weekStart, -7))}><i className="fa-solid fa-chevron-left" /></button>
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{rangeLabel}</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWeekStart(addDays(weekStart, 7))}><i className="fa-solid fa-chevron-right" /></button>
                </div>
                <div className="tt-legend-trigger" tabIndex={0}>
                  <i className="fa-solid fa-circle-info" />
                  <div className="tt-legend-pop">
                    <div className="tt-pt">Legend</div>
                    <div className="leg-row"><div className="leg-sw" style={{ background: 'var(--tt-tint-a)', border: '1px solid var(--tt-tint-a-bd)' }} /> Assigned Session</div>
                    <div className="leg-row"><div className="leg-sw" style={{ background: 'var(--tt-tint-c)', border: '1.5px solid var(--tt-tint-c-bd)' }} /> Trainer Overloaded</div>
                    <div className="leg-row"><div className="leg-sw" style={{ background: 'var(--tt-tint-sea)', border: '1.5px dashed var(--sea-bg)' }} /> Open Slot</div>
                  </div>
                </div>
              </div>

              <div className="tt-gw">
                {activeTab === 'week' && (
                  <div className="tt-grid" style={{ gridTemplateColumns: '82px repeat(7,1fr)' }}>
                    <div className="tt-cor">TIME</div>
                    {days.map((d) => <div key={d.id} className="tt-dh"><span>{d.date}</span>{d.label}</div>)}
                    {rows.map((row) => (
                      <div key={row.time} style={{ display: 'contents' }}>
                        <div className="tt-tl">{row.time}</div>
                        {row.cells.map((cell) => {
                          if (cell.empty) {
                            return (
                              <div key={cell.key} className={`tt-cell ${perms?.create ? 'clickable' : ''}`} onClick={() => openAssign({ session_date: cell.date, start_time: cell.time }, 'date')}>
                                {perms?.create && <div className="mb drop"><i className="fa-solid fa-plus" style={{ marginRight: '.3rem' }} />Add Session</div>}
                              </div>
                            );
                          }
                          const s = cell.session;
                          return (
                            <div key={cell.key} className={`tt-cell ${perms?.update ? 'clickable' : ''}`} onClick={() => openEdit(s, 'date')}>
                              <div className={`mb ${cell.overloaded ? 'conflict' : 'assigned'}`}>
                                <div>
                                  <div className="mc" style={{ color: cell.overloaded ? 'var(--red)' : 'var(--ocean)' }}>{s.module_code}</div>
                                  <div className="mn">{moduleName(s.module_code)}</div>
                                  <div className="mi">
                                    <i className="fa-solid fa-user" style={{ fontSize: '.62rem' }} />{shortName(trainerName(s.trainer_id))}
                                    <i className="fa-solid fa-door-open" style={{ fontSize: '.62rem' }} />{s.room || '—'}
                                  </div>
                                </div>
                                {cell.overloaded && <div className="mct"><i className="fa-solid fa-triangle-exclamation" /> AI: Overloaded</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'lecturer' && (
                  <div className="lv-grid" style={{ gridTemplateColumns: '220px repeat(7,1fr)' }}>
                    <div className="tt-cor">TRAINER</div>
                    {days.map((d) => <div key={d.id} className="tt-dh"><span>{d.date}</span>{d.label}</div>)}
                    {!trainers.length && <div style={{ gridColumn: '1 / -1', padding: '2rem', color: 'var(--slate)' }}>No trainers found.</div>}
                    {lvRows.map((lr) => (
                      <div key={lr.trainer.id} style={{ display: 'contents' }}>
                        <div className="lv-id">
                          <div className="lv-id-av" style={{ background: 'linear-gradient(135deg,var(--brand),var(--brand-end))' }}>{initials(lr.trainer.name)}</div>
                          <div>
                            <div className="lv-id-n">{shortName(lr.trainer.name)}</div>
                            <div className="lv-id-s">{statById[lr.trainer.id]?.hours ?? 0}h this week</div>
                          </div>
                        </div>
                        {lr.cells.map((cell) => (
                          <div key={cell.date} className="lv-cell">
                            {cell.chips.map((ch) => (
                              <div key={ch.session.id} className={`lv-chip ${ch.overloaded ? 'overload' : ''}`} onClick={() => openEdit(ch.session, 'trainer')}>
                                <div className="lv-chip-code">{ch.session.module_code}</div>
                                <div className="lv-chip-time">{normTime(ch.session.start_time)} · {ch.session.room || '—'}</div>
                              </div>
                            ))}
                            {perms?.create && (
                              <button type="button" className="lv-add" title="Add session" onClick={() => openAssign({ trainer_id: lr.trainer.id, session_date: cell.date }, 'trainer')}>
                                <i className="fa-solid fa-plus" style={{ fontSize: '.6rem' }} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'room' && (
                  <div className="lv-grid" style={{ gridTemplateColumns: '220px repeat(7,1fr)' }}>
                    <div className="tt-cor">ROOM</div>
                    {days.map((d) => <div key={d.id} className="tt-dh"><span>{d.date}</span>{d.label}</div>)}
                    {!venues.length && <div style={{ gridColumn: '1 / -1', padding: '2rem', color: 'var(--slate)' }}>No active venues found.</div>}
                    {rmRows.map((rr) => (
                      <div key={rr.venue.id} style={{ display: 'contents' }}>
                        <div className="lv-id">
                          <div className="lv-id-av" style={{ background: 'linear-gradient(135deg,var(--gold-bg),var(--gold-end))' }}>{initials(rr.venue.name)}</div>
                          <div>
                            <div className="lv-id-n">{rr.venue.name}</div>
                            {!!rr.venue.is_outdoor && rr.venue.name.toLowerCase() !== 'outdoor' && <div className="lv-id-s"><i className="fa-solid fa-sun" style={{ fontSize: '.6rem' }} /> Outdoor</div>}
                          </div>
                        </div>
                        {rr.cells.map((cell) => (
                          <div key={cell.date} className="lv-cell">
                            {cell.chips.map((ch) => (
                              <div key={ch.session.id} className={`lv-chip ${ch.overloaded ? 'overload' : ''}`} onClick={() => openEdit(ch.session, 'room')}>
                                <div className="lv-chip-code">{ch.session.module_code}</div>
                                <div className="lv-chip-time">{normTime(ch.session.start_time)} · {shortName(trainerName(ch.session.trainer_id))}</div>
                              </div>
                            ))}
                            {perms?.create && (
                              <button type="button" className="lv-add" title="Add session" onClick={() => openAssign({ room: rr.venue.name, session_date: cell.date }, 'room')}>
                                <i className="fa-solid fa-plus" style={{ fontSize: '.6rem' }} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="tt-sb">
              <div className="tt-panel">
                <div className="tt-pt"><i className="fa-solid fa-chalkboard-user" style={{ marginRight: '.35rem' }} />Trainers</div>
                {!trainerStats.length && <div style={{ color: 'var(--slate)', fontSize: 'var(--text-sm)' }}>No trainer accounts yet.</div>}
                {sortedTrainerStats.map((t) => (
                  <div key={t.id} className="ri" style={{ flexWrap: 'wrap' }}>
                    <div className="ri-av" style={{ background: 'linear-gradient(135deg,var(--brand),var(--brand-end))', color: '#fff' }}>{initials(t.name)}</div>
                    <div className="ri-in"><div className="ri-n">{shortName(t.name)}</div></div>
                    <div className={`ri-l ${t.overloaded ? 'lr-ov' : t.pct >= 85 ? 'lw-load' : 'lo'}`}>{t.hours}h</div>
                    <div className="ri-bar-track"><div className="ri-bar-fill" style={{ width: `${t.pct}%`, background: t.overloaded ? 'var(--red)' : t.pct >= 85 ? 'var(--gold)' : 'var(--sea)' }} /></div>
                    {t.maxRun >= 3 && (
                      <span className={`ri-chip ${t.maxRun >= 4 ? 'crit' : 'warn'}`}>
                        <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '.6rem' }} />
                        {t.maxRun >= 4 ? `Overused — Back-to-back ×${t.maxRun}` : `Back-to-back ×${t.maxRun}`}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div className="tt-panel">
                <div className="tt-pt"><i className="fa-solid fa-door-open" style={{ marginRight: '.35rem' }} />Venues</div>
                {!venues.length && <div style={{ color: 'var(--slate)', fontSize: 'var(--text-sm)' }}>No active venues found.</div>}
                {sortedVenueStats.slice(0, 8).map((v) => (
                  <div key={v.id} className="ri">
                    <div className="ri-av" style={{ background: 'var(--chip-bg)', color: 'var(--ink-s)', fontSize: '.62rem' }}>{initials(v.name)}</div>
                    <div className="ri-in"><div className="ri-n">{v.name}</div>{!!v.is_outdoor && v.name.toLowerCase() !== 'outdoor' && <div className="ri-s">Outdoor</div>}</div>
                    <div className="ri-l lo">{v.util} session{v.util !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <AiAssistantPanel
            weekStartIso={weekStartIso}
            weekEndIso={weekEndIso}
            rangeLabel={rangeLabel}
            onViewLecturer={() => setActiveTab('lecturer')}
            onViewRoom={() => setActiveTab('room')}
          />
        </>
      )}

      {modal && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeModal}>
          <form className="card modal" style={{ width: 640, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <div className="modal-header">
              <div className="modal-title">{modal.mode === 'create' ? 'Schedule Session' : 'Edit Session'}</div>
              <button type="button" className="modal-close" onClick={closeModal} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              {formError && <div className="lc-err">{formError}</div>}
              <div className="modal-row" style={{ display: 'flex', gap: '.8rem' }}>
                <div className="fg fg-tight" style={{ flex: 1, minWidth: 0 }}>
                  <label className="fl">Intake *</label>
                  <SearchableSelect className="finput" required value={modal.intake_code} onChange={onIntakeChange} placeholder="Select intake…" options={intakeOptions} />
                </div>
                <div className="fg fg-tight" style={{ flex: 1, minWidth: 0 }}>
                  <label className="fl">Module *</label>
                  <SearchableSelect
                    className="finput" required value={modal.module_code} onChange={(v) => patchModal({ module_code: v })}
                    placeholder={!modal.intake_code ? 'Select intake first…' : modal.intakeModulesLoading ? 'Loading modules…' : 'Select module…'}
                    options={moduleOptions} disabled={!modal.intake_code || modal.intakeModulesLoading}
                  />
                  {modal.intake_code && !modal.intakeModulesLoading && !moduleOptions.length && (
                    <div className="lock-hint"><i className="fa-solid fa-circle-info" style={{ fontSize: '.65rem' }} />No modules are assigned to this intake yet.</div>
                  )}
                </div>
              </div>

              <div className="fg fg-tight">
                <label className="fl">Trainer *</label>
                <SearchableSelect
                  className="finput" required value={modal.trainer_id} onChange={(v) => patchModal({ trainer_id: v })}
                  placeholder="Select trainer…" options={trainerOptions} disabled={modal.lock === 'trainer'}
                />
                {modal.lock === 'trainer' && <div className="lock-hint"><i className="fa-solid fa-lock" style={{ fontSize: '.65rem' }} />Locked to this row — reassign the trainer from Week View or Room View.</div>}
              </div>

              {modal.lock !== 'trainer' && (
                <>
                  <button type="button" className="btn btn-ai btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={aiSuggest} disabled={aiSuggestLoading}>
                    <i className={`fa-solid ${aiSuggestLoading ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'}`} /> {aiSuggestLoading ? 'Thinking…' : 'AI Suggest Trainer'}
                  </button>
                  {aiSuggestion && (
                    <div style={{ background: 'var(--tt-tint-a)', border: '1px solid var(--tt-tint-a-bd)', borderRadius: 'var(--rs)', padding: '.85rem .95rem', marginTop: '.7rem', display: 'flex', alignItems: 'center', gap: '.75rem' }}>
                      <div style={{ width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg,#1E3A8A,#1D4ED8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                        <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: '.8rem' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, color: 'var(--ink)' }}>{aiSuggestion.name} <span style={{ fontWeight: 500, color: 'var(--slate)' }}>· {aiSuggestion.room}</span></div>
                        <div style={{ fontSize: '.72rem', color: 'var(--slate)', marginTop: '.1rem' }}>{aiSuggestion.reason}</div>
                      </div>
                      <button type="button" className="btn btn-outline btn-sm" onClick={useSuggestion}><i className="fa-solid fa-check" /> Use this</button>
                    </div>
                  )}
                </>
              )}

              <div className="fg fg-tight" style={{ marginTop: '1.05rem' }}>
                <label className="fl">Date *</label>
                <input
                  type="date" className="finput" required value={modal.session_date}
                  disabled={modal.lock === 'date'}
                  onChange={(e) => patchModal({ session_date: e.target.value })}
                />
                {modal.lock === 'date' && <div className="lock-hint"><i className="fa-solid fa-lock" style={{ fontSize: '.65rem' }} />Locked to the cell you clicked — reassign the date from Lecturer View or Room View.</div>}
              </div>
              <div style={{ display: 'flex', gap: '.8rem' }}>
                <div className="fg fg-tight" style={{ flex: 1 }}>
                  <label className="fl">From *</label>
                  <input type="time" className="finput" required value={modal.start_time} onChange={(e) => onStartTimeChange(e.target.value)} />
                </div>
                <div className="fg fg-tight" style={{ flex: 1 }}>
                  <label className="fl">To *</label>
                  <input type="time" className="finput" required value={modal.end_time} onChange={(e) => patchModal({ end_time: e.target.value })} />
                </div>
              </div>
              {modalDurationMinutes > 0 && (
                <div className="lock-hint" style={{ marginTop: '-.6rem', marginBottom: '1.05rem' }}>
                  <i className="fa-solid fa-hourglass-half" style={{ fontSize: '.65rem' }} />Duration: {formatDuration(modalDurationMinutes)}
                </div>
              )}
              {modal.start_time && modal.end_time && modalDurationMinutes <= 0 && (
                <div className="lock-hint" style={{ marginTop: '-.6rem', marginBottom: '1.05rem', color: 'var(--red)' }}>
                  <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '.65rem' }} />End time must be after start time.
                </div>
              )}

              <div className="fg fg-tight">
                <label className="fl">Room *</label>
                <SearchableSelect
                  className="finput" required value={modal.room} onChange={(v) => patchModal({ room: v })}
                  placeholder="Select room…" options={roomOptions} disabled={modal.lock === 'room'}
                />
                {modal.lock === 'room' && <div className="lock-hint"><i className="fa-solid fa-lock" style={{ fontSize: '.65rem' }} />Locked to this row — reassign the room from Week View or Lecturer View.</div>}
                {!modal.lock && modal.room && venueByName(modal.room)?.is_outdoor && <div className="lock-hint"><i className="fa-solid fa-sun" style={{ fontSize: '.65rem' }} />Outdoor venue.</div>}
              </div>

              <div style={{ display: 'flex', gap: '.8rem' }}>
                <div className="fg fg-tight" style={{ flex: 1 }}>
                  <label className="fl">Repeat</label>
                  <SearchableSelect
                    className="finput" value={modal.repeat} onChange={(v) => patchModal({ repeat: v, repeatUntil: v === 'none' ? '' : modal.repeatUntil })}
                    options={[
                      { value: 'none', label: 'Does not repeat' },
                      { value: 'daily', label: 'Every day (Mon–Fri)' },
                      { value: 'weekly', label: modal.session_date ? `Every week on ${weekdayName(modal.session_date)}` : 'Every week' },
                    ]}
                  />
                </div>
                {modal.repeat !== 'none' && (
                  <div className="fg fg-tight" style={{ flex: 1 }}>
                    <label className="fl">Repeat until *</label>
                    <input type="date" className="finput" required min={modal.session_date} value={modal.repeatUntil} onChange={(e) => patchModal({ repeatUntil: e.target.value })} />
                  </div>
                )}
              </div>
              {modal.mode === 'edit' && modal.repeat !== 'none' && (
                <div className="lock-hint" style={{ marginTop: '-.6rem' }}>
                  <i className="fa-solid fa-circle-info" style={{ fontSize: '.65rem' }} />
                  This session keeps its own date — new sessions will be added for the other occurrences.
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <div>
                {modal.mode === 'edit' && perms?.delete && (
                  <button type="button" className="btn btn-danger" onClick={askRemove} title="Remove Session" aria-label="Remove Session">
                    <i className="fa-solid fa-trash" />
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '.6rem' }}>
                <button type="button" className="btn btn-ghost" onClick={closeModal}>
                  <i className="fa-solid fa-xmark" /> Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting || saveDisabled}>
                  <i className={`fa-solid ${submitting ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                  {submitting ? 'Saving…' : modal.mode === 'create' ? 'Schedule Session' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove Session"
        message={`Remove the session ${deleteTarget?.label}? This cannot be undone.`}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
